import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api-client";
import type { CategoryPresets } from "@capture/shared";

/**
 * Plan 4 Task 4 — CRUD hook for JobTypeCategory rows.
 *
 * Wraps `/api/job-type-categories`:
 *   - GET    /                  → list (tenant-scoped, ordered)
 *   - POST   /   {name,...}     → create
 *   - PATCH  /:id {name?,...}   → update
 *   - DELETE /:id               → delete (409 `category_in_use` when jobs
 *                                 reference it; caller surfaces the toast)
 *
 * `remove` does NOT swallow the 409 — it rethrows the ApiError so the list
 * page can detect `category_in_use` and show an inline "in use" message
 * instead of a generic failure toast.
 */

export interface JobTypeCategory {
  id: string;
  tenantId: string;
  name: string;
  displayOrder: number;
  presets: CategoryPresets;
  createdAt: string;
  updatedAt: string;
}

export interface CategoryWriteBody {
  name: string;
  displayOrder?: number;
  presets?: CategoryPresets;
}

interface UseJobTypeCategoriesReturn {
  categories: JobTypeCategory[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  create: (body: CategoryWriteBody) => Promise<JobTypeCategory>;
  update: (id: string, body: Partial<CategoryWriteBody>) => Promise<JobTypeCategory>;
  remove: (id: string) => Promise<void>;
}

export function useJobTypeCategories(): UseJobTypeCategoriesReturn {
  const [categories, setCategories] = useState<JobTypeCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await api.get<JobTypeCategory[]>("/job-type-categories");
      // Defensive: never let a malformed/empty response make `categories` a
      // non-array — JobTypeListPage does `categories.map(...)` and would crash
      // the whole Job Types list. Always resolve to an array.
      setCategories(Array.isArray(data) ? data : []);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const create = useCallback(async (body: CategoryWriteBody) => {
    return api.post<JobTypeCategory>("/job-type-categories", body);
  }, []);

  const update = useCallback(
    async (id: string, body: Partial<CategoryWriteBody>) => {
      return api.patch<JobTypeCategory>(`/job-type-categories/${id}`, body);
    },
    [],
  );

  // Rethrows on failure (incl. 409 category_in_use) — the caller decides how
  // to present it. Tenant isolation + the in-use guard live server-side.
  const remove = useCallback(async (id: string) => {
    await api.delete(`/job-type-categories/${id}`);
  }, []);

  return { categories, isLoading, error, refresh, create, update, remove };
}
